import { KeyboardEvent, useState } from 'react';
import { Delete, RotateCcw } from 'lucide-react';
import { evaluateExpression } from '../utils/calculator';

const buttons = ['sin', 'cos', 'tan', 'log', 'ln', 'sqrt', '(', ')', '^', '%', '7', '8', '9', '/', 'MC', '4', '5', '6', '*', 'C', '1', '2', '3', '-', '⌫', '0', '.', '=', '+'];

export function ScientificCalculator() {
  const [display, setDisplay] = useState('0');
  const [memory, setMemory] = useState('');
  const [error, setError] = useState('');

  const append = (value: string) => {
    setError('');
    setDisplay((current) => (current === '0' ? value : `${current}${value}`));
  };

  const evaluate = () => {
    try {
      const value = evaluateExpression(display);
      setDisplay(String(value));
      setMemory(String(value));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid expression');
    }
  };

  const handleButton = (value: string) => {
    if (value === '=') evaluate();
    else if (value === 'C') {
      setDisplay('0');
      setError('');
    } else if (value === 'MC') {
      setMemory('');
    } else if (value === '⌫') {
      setDisplay((current) => (current.length <= 1 ? '0' : current.slice(0, -1)));
    } else if (['sin', 'cos', 'tan', 'log', 'ln', 'sqrt'].includes(value)) append(`${value}(`);
    else append(value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      evaluate();
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-black/35 p-3 shadow-neon">
      <label className="sr-only" htmlFor="scientific-calculator">Scientific calculator expression</label>
      <input
        id="scientific-calculator"
        className="w-full rounded-md border border-white/10 bg-slate-950/90 px-3 py-3 text-right font-mono text-lg text-white outline-none focus:border-neon-blue"
        value={display}
        onChange={(event) => setDisplay(event.target.value)}
        onKeyDown={onKeyDown}
        aria-invalid={Boolean(error)}
      />
      <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
        <span>Memory {memory || 'empty'}</span>
        {error ? <span role="alert" className="text-amber-300">{error}</span> : null}
      </div>
      <div className="mt-3 grid grid-cols-5 gap-2">
        {buttons.map((button) => (
          <button
            key={button}
            type="button"
            onClick={() => handleButton(button)}
            className="focus-ring min-h-10 rounded-md border border-white/10 bg-white/[0.08] px-2 text-sm font-semibold text-slate-100 transition hover:border-neon-violet hover:bg-neon-violet/20"
            aria-label={button === '⌫' ? 'Backspace' : button === 'C' ? 'Clear' : button === 'MC' ? 'Memory clear' : button}
          >
            {button === '⌫' ? <Delete className="mx-auto h-4 w-4" /> : button === 'C' ? <RotateCcw className="mx-auto h-4 w-4" /> : button}
          </button>
        ))}
      </div>
    </div>
  );
}
